import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Users2, Sparkles, HeartHandshake, AlertTriangle, ClipboardList, Calendar, Activity, ArrowRight, Phone, Mail, MessageCircle, X, Clock, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const _fmtWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const _hoursLeft = (iso) => {
  if (!iso) return null;
  try {
    const diffMs = new Date(iso).getTime() - Date.now();
    if (diffMs <= 0) return "overdue";
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    return h >= 1 ? `${h}h ${m}m left` : `${m}m left`;
  } catch { return null; }
};

const _telHref = (phone) => (phone ? `tel:${String(phone).replace(/\s+/g, "")}` : null);
const _waHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
};

/**
 * Admin home page. Designed as a "top-1% ops dashboard" — hero KPIs first, followed
 * by an actionable "needs attention" panel, a week-ahead mini calendar, and a live
 * activity feed of the last 20 audit events. Powered by GET /api/admin/overview.
 */
export default function AdminOverview({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsBooking, setDetailsBooking] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/overview")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="brand-card p-8 animate-pulse h-96" />;
  if (!data) return <div className="brand-card p-8 text-[#7A7D96]">Could not load overview.</div>;

  const k = data.kpis || {};
  const a = data.attention || {};
  return (
    <div className="space-y-8" data-testid="admin-overview">
      {/* Hero: 4 KPI tiles + section title */}
      <div>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h1 className="text-3xl font-extrabold text-[#3D405B]">Overview</h1>
            <p className="text-sm text-[#7A7D96] mt-1">Everything you need at a glance — this month, and what needs your attention today.</p>
          </div>
          <span className="pill bg-secondary/15 text-secondary hidden md:inline-flex">Live</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <HeroTile icon={Users2} label="Active clients this month" value={k.active_clients_month} tone="primary" testid="overview-kpi-clients" />
          <HeroTile icon={Sparkles} label="Sessions delivered" value={k.sessions_delivered_month} tone="secondary" testid="overview-kpi-sessions" />
          <HeroTile icon={HeartHandshake} label="Hours donated YTD" value={k.hours_donated_ytd} tone="accent" testid="overview-kpi-hours" />
          <HeroTile icon={AlertTriangle} label="Pending admin actions" value={k.pending_actions} tone={k.pending_actions > 0 ? "warning" : "muted"} testid="overview-kpi-pending" />
        </div>
      </div>

      {/* Attention panel + week ahead — 2/3 + 1/3 split on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="brand-card p-6" data-testid="overview-attention">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-extrabold text-[#3D405B]">Needs your attention</h2>
              <ClipboardList size={18} className="text-primary" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <AttentionCard
                title="Pending applications"
                count={a.pending_applications}
                cta="Review"
                onClick={() => onNavigate?.("applications")}
                testid="attention-applications"
              />
              <AttentionCard
                title="Awaiting counsellor accept"
                count={a.pending_acceptances}
                cta="See bookings"
                onClick={() => onNavigate?.("bookings")}
                testid="attention-acceptances"
              />
              <AttentionCard
                title="Feedback outstanding"
                count={a.needs_outcome}
                cta="See attendance"
                onClick={() => onNavigate?.("reports:attendance")}
                testid="attention-feedback"
              />
            </div>
          </section>

          <section className="brand-card p-6" data-testid="overview-upcoming-sessions">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-extrabold text-[#3D405B]">Upcoming sessions</h2>
                <p className="text-xs text-[#7A7D96] mt-1">Next 10 bookings — tap <span className="font-bold text-primary">Details</span> to reach the client or counsellor.</p>
              </div>
              <Calendar size={18} className="text-primary" />
            </div>
            <ul className="divide-y divide-[#3D405B]/5">
              {(data.upcoming_sessions || []).length === 0 && (
                <li className="text-sm text-[#7A7D96] py-3">No sessions in the queue.</li>
              )}
              {(data.upcoming_sessions || []).map((s) => {
                const isPending = s.status === "pending_acceptance";
                return (
                  <li key={s.booking_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`upcoming-row-${s.booking_id}`}>
                    <StatusBadge pending={isPending} />
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-extrabold text-[#3D405B]">
                        {s.client?.name || "—"} <span className="text-[#7A7D96] font-normal">→</span> {s.counsellor?.name || "—"}
                      </div>
                      <div className="text-xs text-[#7A7D96] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{_fmtWhen(s.scheduled_at)}</span>
                        {s.session_mode && <span className="pill bg-muted/50 text-[#3D405B] text-[10px]">{s.session_mode === "in_person" ? "In-person" : "Online"}</span>}
                        {isPending && s.acceptance_deadline && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#B84A3E]">
                            <Clock size={11} /> {_hoursLeft(s.acceptance_deadline)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDetailsBooking(s)}
                      className="text-xs font-extrabold text-primary hover:underline inline-flex items-center gap-1"
                      data-testid={`upcoming-details-${s.booking_id}`}
                    >
                      Details <ArrowRight size={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="brand-card p-6" data-testid="overview-activity">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-extrabold text-[#3D405B]">Recent activity</h2>
              <Activity size={18} className="text-secondary" />
            </div>
            <ul className="space-y-2 max-h-96 overflow-y-auto">
              {(data.recent_activity || []).length === 0 && (
                <li className="text-sm text-[#7A7D96]">No activity yet.</li>
              )}
              {(data.recent_activity || []).map((r) => (
                <li key={r.audit_id} className="flex items-start gap-3 text-sm py-2 border-b border-[#3D405B]/5 last:border-b-0">
                  <span className="w-2 h-2 mt-1.5 rounded-full bg-primary/40 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[#3D405B]">
                      <span className="font-bold">{r.actor_name}</span> · <span className="text-[#5F627D]">{(r.action || "").replace(/_/g, " ")}</span>
                    </div>
                    {r.target_label && <div className="text-xs text-[#7A7D96] truncate">{r.target_label}</div>}
                  </div>
                  <div className="text-[11px] text-[#7A7D96] whitespace-nowrap">{_fmtWhen(r.created_at)}</div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section className="brand-card p-6" data-testid="overview-week-ahead">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-extrabold text-[#3D405B]">Week ahead</h2>
            <Calendar size={18} className="text-primary" />
          </div>
          <div className="space-y-2">
            {(data.week_ahead || []).map((day) => {
              const total = day.scheduled + day.pending;
              const maxTotal = Math.max(1, ...(data.week_ahead || []).map((d) => d.scheduled + d.pending));
              const pctScheduled = ((day.scheduled / maxTotal) * 100).toFixed(0);
              const pctPending = ((day.pending / maxTotal) * 100).toFixed(0);
              return (
                <div key={day.date} className="flex items-center gap-3" data-testid={`overview-day-${day.date}`}>
                  <div className="w-14 text-[11px] font-bold uppercase tracking-[0.14em] text-[#7A7D96] shrink-0">
                    {new Date(day.date + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}
                  </div>
                  <div className="flex-1 h-6 bg-muted/40 rounded-full overflow-hidden flex">
                    <div className="bg-secondary h-full" style={{ width: `${pctScheduled}%` }} />
                    <div className="bg-primary/40 h-full" style={{ width: `${pctPending}%` }} />
                  </div>
                  <div className="w-8 text-right text-sm font-extrabold text-[#3D405B]">{total}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[#7A7D96] mt-3">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-secondary" /> Scheduled</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary/40" /> Pending</span>
          </div>
          <Link
            to="/admin?tab=reports&sub=roster"
            className="mt-4 inline-flex items-center gap-1 text-xs font-extrabold text-primary hover:underline"
            data-testid="overview-open-roster"
          >
            Open full roster <ArrowRight size={12} />
          </Link>
        </section>
      </div>

      {detailsBooking && (
        <ContactDetailsModal booking={detailsBooking} onClose={() => setDetailsBooking(null)} />
      )}
    </div>
  );
}

function HeroTile({ icon: Icon, label, value, tone, testid }) {
  const toneClasses = {
    primary: "border-primary/25 bg-gradient-to-br from-primary/10 via-white to-primary/5",
    secondary: "border-secondary/30 bg-gradient-to-br from-secondary/10 via-white to-secondary/5",
    accent: "border-accent/40 bg-gradient-to-br from-accent/20 via-white to-accent/5",
    warning: "border-destructive/30 bg-gradient-to-br from-destructive/10 via-white to-destructive/5",
    muted: "border-[#3D405B]/10 bg-white",
  }[tone] || "";
  const valueTone = {
    primary: "text-primary",
    secondary: "text-secondary",
    accent: "text-[#3D405B]",
    warning: "text-destructive",
    muted: "text-[#3D405B]",
  }[tone] || "text-[#3D405B]";
  return (
    <div className={`brand-card p-5 ${toneClasses}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase font-extrabold tracking-[0.16em] text-[#7A7D96]">
        <Icon size={12} /> {label}
      </div>
      <div className={`text-4xl font-extrabold mt-2 ${valueTone}`}>{value ?? 0}</div>
    </div>
  );
}

function AttentionCard({ title, count, cta, onClick, testid }) {
  const hot = (count || 0) > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 transition-all ${hot ? "border-primary/30 bg-primary/5 hover:-translate-y-0.5" : "border-[#3D405B]/10 bg-muted/30 opacity-70"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase font-extrabold tracking-[0.16em] text-[#7A7D96]">{title}</div>
      <div className={`text-3xl font-extrabold mt-1 ${hot ? "text-primary" : "text-[#3D405B]"}`}>{count ?? 0}</div>
      <div className={`inline-flex items-center gap-1 mt-2 text-xs font-extrabold ${hot ? "text-primary" : "text-[#7A7D96]"}`}>
        {cta} <ArrowRight size={12} />
      </div>
    </button>
  );
}

function StatusBadge({ pending }) {
  if (pending) {
    return (
      <span
        className="pill inline-flex items-center gap-1 bg-[#FFF3E4] text-[#B84A3E] border border-[#E07A5F]/30"
        data-testid="badge-awaiting"
      >
        <Clock size={11} /> Awaiting counsellor
      </span>
    );
  }
  return (
    <span
      className="pill inline-flex items-center gap-1 bg-secondary/15 text-secondary border border-secondary/30"
      data-testid="badge-accepted"
    >
      <CheckCircle2 size={11} /> Accepted
    </span>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  if (!value) return <div className="text-xs text-[#7A7D96]">{label}: —</div>;
  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
        className="inline-flex items-center gap-2 text-sm text-primary font-bold hover:underline"
        data-testid={testid}
      >
        <Icon size={14} /> {value}
      </a>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 text-sm text-[#3D405B]">
      <Icon size={14} className="text-[#7A7D96]" /> {value}
    </div>
  );
}

function PartyCard({ role, person, testid }) {
  return (
    <div className="rounded-2xl border border-[#3D405B]/10 p-4 bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase font-extrabold tracking-[0.16em] text-[#7A7D96]">{role}</div>
      <div className="text-base font-extrabold text-[#3D405B] mt-1">{person?.name || "—"}</div>
      <div className="mt-3 grid gap-2">
        <ContactRow icon={Phone} label="Phone" value={person?.phone} href={_telHref(person?.phone)} testid={`${testid}-call`} />
        <ContactRow icon={MessageCircle} label="WhatsApp" value={person?.phone ? "WhatsApp" : ""} href={_waHref(person?.phone)} testid={`${testid}-wa`} />
        <ContactRow icon={Mail} label="Email" value={person?.email} href={person?.email ? `mailto:${person.email}` : null} testid={`${testid}-email`} />
      </div>
    </div>
  );
}

function ContactDetailsModal({ booking, onClose }) {
  const isPending = booking.status === "pending_acceptance";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="upcoming-details-modal"
    >
      <div
        className="brand-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-extrabold text-[#3D405B]">Confirm & follow up</h3>
            <p className="text-xs text-[#7A7D96] mt-1">Reach out to both parties to confirm attendance for the upcoming session.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#7A7D96] hover:text-[#3D405B]"
            data-testid="upcoming-details-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="rounded-2xl bg-muted/30 p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase font-extrabold tracking-[0.16em] text-[#7A7D96]">Session</div>
            <div className="text-sm font-extrabold text-[#3D405B] mt-1">{_fmtWhen(booking.scheduled_at)}</div>
            {booking.topic && <div className="text-xs text-[#7A7D96] mt-1">Topic: {booking.topic}</div>}
          </div>
          <StatusBadge pending={isPending} />
        </div>

        {isPending && booking.acceptance_deadline && (
          <div className="rounded-2xl bg-[#FFF3E4] border border-[#E07A5F]/30 p-3 mb-4 text-xs text-[#B84A3E] font-bold">
            <Clock size={12} className="inline mr-1" /> Counsellor has {_hoursLeft(booking.acceptance_deadline)} to accept.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PartyCard role="Client" person={booking.client} testid="modal-client" />
          <PartyCard role="Counsellor" person={booking.counsellor} testid="modal-counsellor" />
        </div>

        <div className="mt-4 text-[11px] text-[#7A7D96]">
          Use the phone, WhatsApp or email links above to reach either party and confirm they'll attend.
        </div>
      </div>
    </div>
  );
}
